import { FC, useMemo, useCallback, MouseEvent } from 'react'
import styles from 'components/features/future/FutureLayerPreview/FutureLayerPreview.styl'
import { IconCircled } from 'components/features/pfm/IconCircled/IconCircled'
import { InputToggle } from 'components/features/pfm/InputToggle/InputToggle'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import moment from 'moment'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { layerSelector, enabledLayersSelector } from 'components/features/future/selectors'
import { patchFutureLayerActionCreator } from 'components/features/future/actions'
import { LAYER_METAS } from 'constants/future'
import { modifyFloatingPaneActionCreator, openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import SvgIcon from 'components/atoms/SvgIcon'
import CheckSvg from 'icons/check.svg'
import cn from 'classnames'

type FutureLayerPreviewProps = {
  uid: FutureLayerUID
}

export const FutureLayerPreview: FC<FutureLayerPreviewProps> = (props) => {
  const layer = useStoreSelector((state) => layerSelector(state, props))
  if (!layer) {
    return null
  }

  return (
    <FutureLayerPreviewInner layer={ layer } />
  )
}

type FutureLayerPreviewInnerProps = {
  layer: FutureLayer
}
const FutureLayerPreviewInner: FC<FutureLayerPreviewInnerProps> = (props) => {
  const { layer } = props
  const { uid, status, slug, name, when_month } = layer
  const { icon, type } = LAYER_METAS[slug]

  const whenChild = useMemo(() => {
    if (!when_month || status !== 'enabled') {
      return 'Excluded from forecast'
    }

    const dateMoment = moment(when_month)
    const month = dateMoment.diff(moment(), 'month')
    if (month < 1) {
      return 'In a month'
    }
    return dateMoment.fromNow()
  }, [when_month, status])

  const enabledLayers = useStoreSelector(enabledLayersSelector)
  const enabledLayersCount = enabledLayers.length
  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)

  const dispatch = useStoreDispatch()
  const handleEnabledClick = useCallback<(event: MouseEvent<HTMLDivElement>) => void>((event) => {
    if (!isMemberPremium && status !== 'enabled' && enabledLayersCount >= 2) {
      event.stopPropagation()
      dispatch(openSubscriptionPageActionCreator())
      return
    }

    if (status === 'past' || status === 'completed') {
      dispatch(modifyFloatingPaneActionCreator('future', {
        mode: 'layer-details',
        uid,
      }))

      return
    }

    dispatch(patchFutureLayerActionCreator.request({
      uid,
      status: status === 'enabled' ? 'disabled' : 'enabled',
    }))
    event.stopPropagation()
  }, [dispatch, uid, status, enabledLayersCount, isMemberPremium])

  const handleLayerEdit = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'layer-details',
      uid,
    }))
  }, [dispatch, uid])

  const toggleSwitch = useMemo(() => {

    if (status === 'past' || status === 'completed' || status === 'partially_completed') {
      const isComplete = status === 'completed' || status === 'partially_completed'
      return (
        <div className={ cn(styles.checkIcon, isComplete && styles.complete) }>
          <SvgIcon src={ CheckSvg } />
        </div>
      )
    }

    const isEnabled = !status || status === 'enabled'
    return (
      <div className={ styles.enabled } onClick={ handleEnabledClick }>
        <InputToggle isEnabled={ isEnabled } />
      </div>
    )
  }, [status, handleEnabledClick])

  return (
    <div
      key={ slug }
      className={ styles.layerPreview }
      onClick={ handleLayerEdit }
    >
      <div className={ styles.category }>
        <IconCircled
          iconSlug={ icon }
          theme={ type }
        />
      </div>
      <div className={ styles.body }>
        <div className={ styles.title }>
          { name }
        </div>
        <div className={ styles.note }>
          <div className={ styles.when }>
            { whenChild }
          </div>
          &nbsp;|&nbsp;
          <div className={ styles.edit }>Edit</div>
        </div>
      </div>
      { toggleSwitch }
    </div>
  )
}
